/**
 * Surface 1.6 — Utilisation & billability. Every figure, label, unit and route
 * the page renders resolves through this module or `@/demo/data`; the component
 * carries no literals.
 *
 * PROVENANCE — the deck's own numbers, transcribed as given (see
 * product/px-platform-mockups.md §1.6): billable output 76% (target 80%, +4 pts
 * vs Q1) · blended rate €79/h · blended cost €38/h · delivery margin 52% (Q1
 * 46%) · office margins NL 44%, Kosovo 63%, Group 52% · hours by origin Kosovo
 * 41% / Western EU 44% / agent-executed 15% · the three margin movers.
 *
 * AUTHORED ONCE (the deck names only three margins): the BE and FR margins and
 * the per-office billable / non-billable / cost / charge cells. They are
 * derived from the seed so the table cannot contradict the rest of the demo:
 *   - FTE per office is the seeded headcount (NL 21 · BE 12 · FR 9 · KS 20 = 62).
 *   - Charge rate per office is the mean seeded person rate of that office
 *     (NL ≈ 98 · BE ≈ 89 · FR ≈ 104 · KS ≈ 46 €/h).
 *   - Margin = (charge − loaded cost) / charge, so NL 44% and KS 63% reproduce
 *     the deck exactly and BE 48% / FR 47% sit between them.
 *   - Billable % is FTE-weighted to exactly the deck's group 76%
 *     (21·71 + 12·72 + 9·73 + 20·85 = 4712 = 76% of 62 FTE), and each
 *     non-billable split sums to that office's 100 − billable.
 */
import { GROUP_HEADCOUNT, OFFICES, thinSpace, type OfficeCode } from '@/demo/data'

/* ── Routes — no surface builds an href by hand ──────────────────────────── */

export const POOL_HREF = '/resources/pool'
export const PIPELINE_HREF = '/resources/pipeline'
export const AGENT_LIBRARY_HREF = '/library/agents'
export const VAN_DIJK_MIGRATION_HREF = '/project/van-dijk/migration'
export const VAN_DIJK_TEST_HREF = '/project/van-dijk/test'

/* ── Units ───────────────────────────────────────────────────────────────── */

export const pct = (value: number): string => `${value}%`
export const perHour = (euros: number): string => `€${thinSpace(euros)}/h`
export const hours = (value: number): string => `${thinSpace(value)} h`

/* ── KPI row ─────────────────────────────────────────────────────────────── */

export const UTILISATION_KPIS = {
  billableOutputPct: 76,
  billableTargetPct: 80,
  billableDeltaPts: 4,
  blendedRate: 79,
  blendedCost: 38,
  deliveryMarginPct: 52,
  q1MarginPct: 46,
} as const

/* ── Office table ────────────────────────────────────────────────────────── */

export interface OfficeUtilisationRow {
  key: string
  /** Office code, or null on the group total row. */
  code: OfficeCode | null
  label: string
  /** Utrecht / Antwerp / … — empty on the group row. */
  city: string
  fte: number
  billablePct: number
  /** The 100 − billable remainder, spelled out. */
  nonBillableSplit: string
  loadedCostPerHour: number
  chargeRatePerHour: number
  marginPct: number
  /** Group total — rendered as the table footer, not a peer row. */
  isTotal: boolean
}

interface OfficeFigures {
  billablePct: number
  nonBillableSplit: string
  loadedCostPerHour: number
  chargeRatePerHour: number
  marginPct: number
}

const OFFICE_FIGURES: Record<OfficeCode, OfficeFigures> = {
  NL: {
    billablePct: 71,
    nonBillableSplit: 'pre-sales 13% · training 9% · bench 7%',
    loadedCostPerHour: 55,
    chargeRatePerHour: 98,
    marginPct: 44,
  },
  BE: {
    billablePct: 72,
    nonBillableSplit: 'pre-sales 12% · training 9% · bench 7%',
    loadedCostPerHour: 46,
    chargeRatePerHour: 89,
    marginPct: 48,
  },
  FR: {
    billablePct: 73,
    nonBillableSplit: 'pre-sales 11% · training 9% · bench 7%',
    loadedCostPerHour: 55,
    chargeRatePerHour: 104,
    marginPct: 47,
  },
  KS: {
    billablePct: 85,
    nonBillableSplit: 'training 11% · bench 4%',
    loadedCostPerHour: 17,
    chargeRatePerHour: 46,
    marginPct: 63,
  },
}

export const OFFICE_ROWS: readonly OfficeUtilisationRow[] = OFFICES.map((office) => {
  const figures = OFFICE_FIGURES[office.code]
  return {
    key: office.code,
    code: office.code,
    label: office.name,
    city: office.city,
    fte: office.headcount,
    isTotal: false,
    ...figures,
  }
})

export const GROUP_ROW: OfficeUtilisationRow = {
  key: 'group',
  code: null,
  label: 'Group',
  city: '',
  fte: GROUP_HEADCOUNT,
  billablePct: UTILISATION_KPIS.billableOutputPct,
  nonBillableSplit: 'pre-sales 8% · training 10% · bench 6%',
  loadedCostPerHour: UTILISATION_KPIS.blendedCost,
  chargeRatePerHour: UTILISATION_KPIS.blendedRate,
  marginPct: UTILISATION_KPIS.deliveryMarginPct,
  isTotal: true,
}

/* ── Delivery hours by origin (the stacked bar) ──────────────────────────── */

export type OriginTone = 'nearshore' | 'westernEu' | 'agent'

export interface OriginSegment {
  key: OriginTone
  label: string
  sharePct: number
  /** Rendered after the label, linking to where the claim is evidenced. */
  link?: { text: string; to: string }
}

export const ORIGIN_SEGMENTS: readonly OriginSegment[] = [
  { key: 'nearshore', label: 'Kosovo', sharePct: 41 },
  { key: 'westernEu', label: 'Western EU', sharePct: 44 },
  {
    key: 'agent',
    label: 'Agent-executed,',
    sharePct: 15,
    link: { text: 'human-reviewed', to: AGENT_LIBRARY_HREF },
  },
]

/* ── "What moved the margin" (the mint panel) ────────────────────────────── */

export interface MarginMover {
  key: string
  what: string
  result: string
  to: string
  linkLabel: string
}

export const MARGIN_MOVERS: readonly MarginMover[] = [
  {
    key: 'migration-scripts',
    what: 'Migration scripts drafted by agents',
    result: `${hours(71)} saved`,
    to: VAN_DIJK_MIGRATION_HREF,
    linkLabel: 'Van Dijk · migration',
  },
  {
    key: 'uat-prep',
    what: 'UAT prep',
    result: '5 days → 1',
    to: VAN_DIJK_TEST_HREF,
    linkLabel: 'Van Dijk · test & UAT',
  },
  {
    key: 'kosovo-share',
    what: 'Kosovo share',
    result: '29 → 41%',
    to: PIPELINE_HREF,
    linkLabel: 'Talent pipeline',
  },
]

/* ── Chrome + copy ───────────────────────────────────────────────────────── */

export const UTILISATION_CHROME = {
  moduleLabel: 'PX · Resources',
  breadcrumb: 'Reporting › Utilisation & billability',
  contextPill: 'Q3 2026',
} as const

export const UTILISATION_COPY = {
  title: 'Utilisation & billability',
  lede: 'The number the board reads — are we at target, and is the mix moving to Kosovo as planned?',
  kpis: {
    billable: {
      label: 'Billable output',
      figure: pct(UTILISATION_KPIS.billableOutputPct),
      qualifier: `target ${pct(UTILISATION_KPIS.billableTargetPct)} · +${UTILISATION_KPIS.billableDeltaPts} pts vs Q1`,
    },
    rate: {
      label: 'Blended rate',
      figure: perHour(UTILISATION_KPIS.blendedRate),
      qualifier: `charged across ${GROUP_HEADCOUNT} people, ${OFFICES.length} offices`,
    },
    cost: {
      label: 'Blended cost',
      figure: perHour(UTILISATION_KPIS.blendedCost),
      qualifier: 'loaded cost per billable hour',
    },
    margin: {
      label: 'Delivery margin',
      figure: pct(UTILISATION_KPIS.deliveryMarginPct),
      qualifier: `Q1 ${pct(UTILISATION_KPIS.q1MarginPct)} · agent-assisted work`,
    },
  },
  officeTable: {
    heading: 'Utilisation by office',
    caption: `Billability, loaded cost, charge rate and margin for each of the ${OFFICES.length} offices and the ${GROUP_HEADCOUNT}-person group. Loaded cost and charge rate are per billable hour; margin is (charge − cost) / charge. Each office name opens the resource pool.`,
    columns: {
      office: 'Office',
      fte: 'FTE',
      billable: 'Billable %',
      nonBillable: 'Non-billable split',
      cost: 'Loaded cost / FTE',
      rate: 'Charge rate',
      margin: 'Margin',
    },
    unitNote: 'cost and rate per billable hour',
    officeLinkLabel: (office: string) => `${office} — see these people in the resource pool`,
    totalRowLabel: 'group total',
  },
  origin: {
    heading: 'Delivery hours by origin',
    barLabel: (parts: string) => `Delivery hours by origin — ${parts}`,
    verbatim: 'Agent hours are billed as delivered scope, not as time.',
  },
  movers: {
    heading: 'What moved the margin',
    lede: `Delivery margin ${pct(UTILISATION_KPIS.q1MarginPct)} → ${pct(UTILISATION_KPIS.deliveryMarginPct)} — three compounding sources, each traceable to the work that produced it.`,
  },
  densityNote:
    'Office rows are the drill-down: each name opens the resource pool, which is this round’s affordance — pre-filtering it to the office is not wired yet. Per-person billability is withheld here; it lives on the person profile, where the context to read it sits. The board-pack export is not built this round.',
} as const
